import { useEffect, useState } from "react";
import { toast } from "sonner";
import { TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Loader2, Download, FileText, Star, Heart, ChevronDown, ChevronRight } from "lucide-react";
import { bandMeta } from "@/lib/api";
import { getFeedbackLog, getFeedbackDetail, downloadFeedbackPdf } from "@/lib/api";

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "";

const PAGE = 20;

const Detail = ({ id }) => {
  const [d, setD] = useState(null);
  useEffect(() => {
    getFeedbackDetail(id).then(setD).catch(() => setD(false));
  }, [id]);
  if (d === null) return <div className="py-3"><Loader2 className="h-4 w-4 animate-spin text-clay" /></div>;
  if (d === false) return <p className="py-3 text-sm text-clay">Couldn't load this entry.</p>;
  return (
    <div className="mt-3 space-y-4" data-testid={`feedback-detail-${id}`}>
      <p className="text-ink-soft italic">{d.verdict}</p>
      {(d.questions || []).map((q, i) => (
        <div key={`q-${i}-${q.band}`} className="rounded-xl border border-border p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wide text-ink-faint">Question {i + 1}</span>
            <span className="font-heading" style={{ color: bandMeta(q.band).color }}>{q.band}</span>
          </div>
          <div className="mt-2 grid gap-3 sm:grid-cols-2 text-sm">
            <div>
              <div className="text-forest font-medium mb-1">What's working</div>
              <ul className="list-disc pl-4 space-y-1 text-ink-soft">{(q.working || []).map((w, j) => <li key={`w-${j}-${w}`}>{w}</li>)}</ul>
            </div>
            <div>
              <div className="text-clay font-medium mb-1">How to improve</div>
              <ul className="list-disc pl-4 space-y-1 text-ink-soft">{(q.improvements || []).map((w, j) => <li key={`i-${j}-${w}`}>{w}</li>)}</ul>
            </div>
          </div>
        </div>
      ))}
      {(d.thinking_questions || []).length > 0 && (
        <div className="rounded-xl border border-forest/20 bg-forest/[0.04] p-4">
          <div className="text-sm font-medium text-ink mb-2">Questions to push thinking</div>
          <ol className="list-decimal pl-5 space-y-1 text-sm text-ink-soft">
            {d.thinking_questions.map((q, i) => <li key={`t-${i}-${q}`}>{q}</li>)}
          </ol>
        </div>
      )}
    </div>
  );
};

export const FeedbackLogPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [skip, setSkip] = useState(0);
  const [open, setOpen] = useState(null);
  const [downloading, setDownloading] = useState(null);

  const load = async (s) => {
    setLoading(true);
    try {
      setData(await getFeedbackLog(s, PAGE));
      setSkip(s);
    } catch {
      toast.error("Couldn't load the feedback log.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = async (id, course) => {
    setDownloading(id);
    try {
      const blob = await downloadFeedbackPdf(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `feedback-${course || "ucas"}-${id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Couldn't download the PDF.");
    } finally {
      setDownloading(null);
    }
  };

  const items = data?.items || [];
  const total = data?.total || 0;

  return (
    <TabsContent value="feedbacklog" className="mt-6 space-y-4" data-testid="feedbacklog-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-heading text-xl text-ink">Feedback log</h2>
        <span className="text-sm text-ink-muted">
          {total} stored · auto-deletes after {data?.retention_months ?? 6} months
        </span>
      </div>
      <p className="text-sm text-ink-muted max-w-2xl">
        A copy of each feedback result (feedback only — the student's original answers are never stored). Open a row to read it, or download the PDF.
      </p>

      {loading ? (
        <div className="grid place-items-center py-16"><Loader2 className="h-7 w-7 animate-spin text-clay" /></div>
      ) : items.length === 0 ? (
        <p className="text-sm text-ink-muted">No feedback stored yet.</p>
      ) : (
        <div className="bg-white border border-border rounded-2xl divide-y divide-border">
          {items.map((it) => (
            <div key={it.id} data-testid={`feedback-row-${it.id}`} className="p-4">
              <div className="flex items-center justify-between gap-4">
                <button
                  onClick={() => setOpen(open === it.id ? null : it.id)}
                  data-testid={`feedback-open-${it.id}`}
                  className="flex items-center gap-2 text-left min-w-0"
                >
                  {open === it.id ? <ChevronDown className="h-4 w-4 text-ink-faint shrink-0" /> : <ChevronRight className="h-4 w-4 text-ink-faint shrink-0" />}
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-ink truncate">{it.course || "—"}</span>
                      <span className="text-sm" style={{ color: bandMeta(it.overall_band).color }}>{it.overall_band}</span>
                      {it.wellbeing_flag && <Heart className="h-3.5 w-3.5 text-forest" title="Wellbeing flag" />}
                    </div>
                    <div className="text-xs text-ink-faint mt-0.5">
                      {it.country || "—"}{it.qualification_type ? ` · ${it.qualification_type}` : ""} · {fmtDate(it.created_at)}
                    </div>
                  </div>
                </button>
                <div className="flex items-center gap-3 shrink-0">
                  {it.rating ? (
                    <span className="inline-flex items-center gap-1 text-sm text-ink">
                      <Star className="h-4 w-4 fill-amber-400 text-amber-400" /> {it.rating}
                    </span>
                  ) : (
                    <span className="text-xs text-ink-faint">no rating</span>
                  )}
                  <Button
                    variant="ghost" size="sm"
                    data-testid={`feedback-pdf-${it.id}`}
                    onClick={() => download(it.id, it.course)}
                    disabled={downloading === it.id}
                    className="text-ink-muted hover:text-ink"
                    title="Download PDF"
                  >
                    {downloading === it.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              {open === it.id && <Detail id={it.id} />}
            </div>
          ))}
        </div>
      )}

      {total > PAGE && (
        <div className="flex items-center justify-between">
          <Button variant="outline" size="sm" disabled={skip === 0 || loading} onClick={() => load(Math.max(0, skip - PAGE))} className="border-border">
            Previous
          </Button>
          <span className="text-sm text-ink-muted flex items-center gap-1"><FileText className="h-3.5 w-3.5" /> {skip + 1}–{Math.min(skip + PAGE, total)} of {total}</span>
          <Button variant="outline" size="sm" disabled={skip + PAGE >= total || loading} onClick={() => load(skip + PAGE)} className="border-border">
            Next
          </Button>
        </div>
      )}
    </TabsContent>
  );
};
